import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useCompanyStatsQuery } from './hooks/useCompanyStatsQuery';
import { useCompanyStatsTimeseriesQuery } from './hooks/useCompanyStatsTimeseriesQuery';
import { TimeSeriesMetric } from '../../store/responseTypes/company';
import { readPeriodFromUrl } from './utils/readPeriodFromUrl';
import CompanyKpiGrid from './components/CompanyKpiGrid';
import CompanyTimeseriesChart from './components/CompanyTimeseriesChart';

interface ICompanyStats {
  companyId: string;
}

const CompanyStats: React.FC<ICompanyStats> = ({ companyId }) => {
  const [searchParams] = useSearchParams();
  const { fromDate, toDate, isExplicit: hasExplicitPeriod } =
    readPeriodFromUrl(searchParams);

  const { data: snapshot, isLoading: snapLoading, isError: snapError } =
    useCompanyStatsQuery(companyId);
  const {
    currentData: timeseries,
    isFetching: tsFetching,
    isError: tsError,
  } = useCompanyStatsTimeseriesQuery({ companyId, fromDate, toDate });

  const [activeMetrics, setActiveMetrics] = useState<TimeSeriesMetric[]>([
    'signedAgreements',
    'registeredAgreements',
  ]);

  const onToggleMetric = (m: TimeSeriesMetric) => {
    setActiveMetrics((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
    );
  };

  return (
    <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 12 }}>
      <VStack align="start" spacing={2} mb={{ base: 8, md: 12 }}>
        <Text
          fontSize="xs"
          letterSpacing="0.2em"
          textTransform="uppercase"
          color="gray.400"
          fontWeight={500}
        >
          Company stats
        </Text>
        <Heading
          as="h1"
          fontSize={{ base: '4xl', md: '6xl' }}
          fontWeight={500}
          letterSpacing="-0.025em"
          color="gray.900"
          lineHeight={1.1}
        >
          {snapshot?.stats.companyName ?? '—'}
        </Heading>
        <HStack spacing={3} align="center">
          {hasExplicitPeriod && (
            <Text fontSize="sm" color="gray.500">
              Chart window: {fromDate.slice(0, 10)} → {toDate.slice(0, 10)}
            </Text>
          )}
          {(snapLoading || tsFetching) && (
            <Spinner size="xs" thickness="2px" speed="0.7s" color="gray.400" />
          )}
        </HStack>
      </VStack>

      <Box mb={{ base: 10, md: 14 }}>
        {snapError ? (
          <VStack py={20} spacing={2} align="center">
            <Text color="gray.900" fontSize="md" fontWeight={500}>
              Couldn't load company stats.
            </Text>
            <Text color="gray.500" fontSize="sm">
              The server returned an error. Refresh the page to try again.
            </Text>
          </VStack>
        ) : (
          <CompanyKpiGrid stats={snapshot?.stats} isLoading={snapLoading} />
        )}
      </Box>

      <CompanyTimeseriesChart
        timeseries={timeseries}
        activeMetrics={activeMetrics}
        onToggleMetric={onToggleMetric}
        isLoading={tsFetching}
        isError={tsError}
      />
    </Box>
  );
};

export default CompanyStats;
